import {
    useState
} from "react";
import {
    useWindowDimensions,
    StyleSheet,
    View
} from "react-native";
import {
    ComponentHeader,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    NCoreUIKitTheme,
    RadioButton,
    Separator,
    TextInput,
    Switch,
    Button,
    Field
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";

const NAME = "Field";

const USAGE_CODE = `<Field.Group>
    <Field.Set>
        <Field.Legend>
            Profile
        </Field.Legend>
        <Field.Description>
            Decides how other people see you.
        </Field.Description>
        <Field>
            <Field.Title>
                Full name
            </Field.Title>
            <TextInput
                spreadBehaviour="stretch"
                onChangeText={setName}
                value={name}
            />
        </Field>
        <Field>
            <Field.Title>
                New password
            </Field.Title>
            <TextInput
                onChangeText={setPassword}
                spreadBehaviour="stretch"
                value={password}
                type="danger"
            />
            <Field.Error
                errors={[
                    "At least 8 characters.",
                    "At least one digit."
                ]}
            />
        </Field>
    </Field.Set>
    <Separator
        title="Notifications and visibility"
    />
    <Field.Set>
        <Field.Legend>
            Notifications
        </Field.Legend>
        <Field
            orientation="horizontal"
        >
            <Field.Content>
                <Field.Title>
                    Email notifications
                </Field.Title>
                <Field.Description>
                    Get important account changes by email.
                </Field.Description>
            </Field.Content>
            <Switch
                onPress={handleToggleEmail}
                isChecked={isEmailOn}
            />
        </Field>
    </Field.Set>
</Field.Group>`;

const SET_CODE = `<Field.Set>
    <Field.Legend>
        Profile
    </Field.Legend>
    <Field.Description>
        Decides how other people see you.
    </Field.Description>
    <Field>
        ...
    </Field>
</Field.Set>`;

const GROUP_CODE = `<Field.Group
    orientation="horizontal"
>
    <Field>
        <Field.Title>
            First name
        </Field.Title>
        <TextInput
            spreadBehaviour="stretch"
        />
    </Field>
    <Field>
        <Field.Title>
            Last name
        </Field.Title>
        <TextInput
            spreadBehaviour="stretch"
        />
    </Field>
</Field.Group>`;

const ORIENTATION_CODE = `<Field
    orientation="vertical"
>
    ...
</Field>

<Field
    orientation="horizontal"
>
    ...
</Field>

<Field
    orientation="responsive"
>
    ...
</Field>`;

const CONTENT_CODE = `<Field
    orientation="horizontal"
>
    <Field.Content>
        <Field.Title>
            Weekly summary
        </Field.Title>
        <Field.Description>
            Sent every Monday with last week's summary.
        </Field.Description>
    </Field.Content>
    <Switch
        onPress={handleToggle}
        isChecked={isOn}
    />
</Field>`;

const ERROR_CODE = `<Field>
    <Field.Title>
        New password
    </Field.Title>
    <TextInput
        onChangeText={setPassword}
        spreadBehaviour="stretch"
        value={password}
        type="danger"
    />
    <Field.Error
        errors={[
            "At least 8 characters.",
            "At least one digit."
        ]}
    />
</Field>`;

const CARD_CODE = `<Field.Set>
    <Field.Legend>
        Visibility
    </Field.Legend>
    <RadioButton
        subTitle="Everyone can see your profile."
        onPress={() => setVisibility("public")}
        isChecked={visibility === "public"}
        title="Public"
        variant="card"
    />
    <RadioButton
        subTitle="Only people in the workspace see it."
        onPress={() => setVisibility("team")}
        isChecked={visibility === "team"}
        title="Team only"
        variant="card"
    />
</Field.Set>`;

const SEPARATOR_CODE = `<Field.Set>
    ...
</Field.Set>

<Separator
    title="Notifications and visibility"
/>

<Field.Set>
    ...
</Field.Set>`;

const ORIENTATIONS = [
    "vertical",
    "horizontal",
    "responsive"
] as const;

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("fieldPage.setTitle"),
        id: "set"
    },
    {
        title: t("fieldPage.groupTitle"),
        id: "group"
    },
    {
        title: t("fieldPage.orientationTitle"),
        id: "orientation"
    },
    {
        title: t("fieldPage.contentTitle"),
        id: "content"
    },
    {
        title: t("fieldPage.errorTitle"),
        id: "error"
    },
    {
        title: t("fieldPage.cardTitle"),
        id: "card"
    },
    {
        title: t("fieldPage.separatorTitle"),
        id: "separator"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const FieldPage = () => {
    const {
        t
    } = useDocsI18n();

    const {
        shapeConfigs,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        width: windowWidth
    } = useWindowDimensions();

    const isNarrow = windowWidth < shapeConfigs.compactBreakpoint;

    const [
        fullName,
        setFullName
    ] = useState("Ada Lovelace");

    const [
        username,
        setUsername
    ] = useState("ada.lovelace");

    const [
        password,
        setPassword
    ] = useState("abcde");

    const [
        isEmailOn,
        setIsEmailOn
    ] = useState(true);

    const [
        isWeeklyOn,
        setIsWeeklyOn
    ] = useState(false);

    const [
        visibility,
        setVisibility
    ] = useState("public");

    const handleToggleEmail = () => setIsEmailOn(!isEmailOn);

    const handleToggleWeekly = () => setIsWeeklyOn(!isWeeklyOn);

    const renderVisibility = () => <View
        aria-label={t("fieldPage.visibilityLabel")}
        style={[
            isNarrow ? undefined : stylesheet.row,
            {
                gap: spaces.spacingMd
            }
        ]}
        role="radiogroup"
    >
        <RadioButton
            style={isNarrow ? undefined : stylesheet.choice}
            subTitle={t("fieldPage.visibilityPublicText")}
            title={t("fieldPage.visibilityPublic")}
            onPress={() => setVisibility("public")}
            isChecked={visibility === "public"}
            variant="card"
        />
        <RadioButton
            style={isNarrow ? undefined : stylesheet.choice}
            subTitle={t("fieldPage.visibilityTeamText")}
            title={t("fieldPage.visibilityTeam")}
            onPress={() => setVisibility("team")}
            isChecked={visibility === "team"}
            variant="card"
        />
    </View>;

    return <Article>
        <ComponentHeader
            lead={t("fieldPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("fieldPage.overviewText")}
            </Paragraph>
            <Paragraph>
                {t("fieldPage.overviewNote")}
            </Paragraph>
            <Preview
                caption={t("fieldPage.overviewCaption")}
                isStacked={true}
            >
                <Field.Group>
                    <Field.Set>
                        <Field.Legend>
                            {t("fieldPage.profile")}
                        </Field.Legend>
                        <Field.Description>
                            {t("fieldPage.profileText")}
                        </Field.Description>
                        <Field>
                            <Field.Title>
                                {t("fieldPage.fullName")}
                            </Field.Title>
                            <TextInput
                                onChangeText={setFullName}
                                spreadBehaviour="stretch"
                                value={fullName}
                            />
                        </Field>
                        <Field>
                            <Field.Title>
                                {t("fieldPage.username")}
                            </Field.Title>
                            <TextInput
                                onChangeText={setUsername}
                                spreadBehaviour="stretch"
                                value={username}
                            />
                            <Field.Description>
                                {t("fieldPage.usernameHint")}
                            </Field.Description>
                        </Field>
                        <Field>
                            <Field.Title>
                                {t("fieldPage.newPassword")}
                            </Field.Title>
                            <TextInput
                                onChangeText={setPassword}
                                spreadBehaviour="stretch"
                                variant="hidden"
                                value={password}
                                type="danger"
                            />
                            <Field.Error
                                errors={[
                                    t("fieldPage.ruleLength"),
                                    t("fieldPage.ruleDigit")
                                ]}
                            />
                        </Field>
                    </Field.Set>
                    <Separator
                        title={t("fieldPage.dividerTitle")}
                    />
                    <Field.Set>
                        <Field.Legend>
                            {t("fieldPage.notifications")}
                        </Field.Legend>
                        <Field
                            orientation="horizontal"
                        >
                            <Field.Content>
                                <Field.Title>
                                    {t("fieldPage.emailTitle")}
                                </Field.Title>
                                <Field.Description>
                                    {t("fieldPage.emailText")}
                                </Field.Description>
                            </Field.Content>
                            <Switch
                                onPress={handleToggleEmail}
                                isChecked={isEmailOn}
                            />
                        </Field>
                        <Field
                            orientation="horizontal"
                        >
                            <Field.Content>
                                <Field.Title>
                                    {t("fieldPage.weeklyTitle")}
                                </Field.Title>
                                <Field.Description>
                                    {t("fieldPage.weeklyText")}
                                </Field.Description>
                            </Field.Content>
                            <Switch
                                onPress={handleToggleWeekly}
                                isChecked={isWeeklyOn}
                            />
                        </Field>
                        {renderVisibility()}
                    </Field.Set>
                    <View
                        style={[
                            stylesheet.footer,
                            {
                                gap: spaces.spacingSm
                            }
                        ]}
                    >
                        <Button
                            title={t("fieldPage.cancel")}
                            onPress={() => undefined}
                            variant="tonal"
                            type="neutral"
                        />
                        <Button
                            title={t("fieldPage.save")}
                            onPress={() => undefined}
                        />
                    </View>
                </Field.Group>
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("fieldPage.setTitle")}
            id="set"
        >
            <Paragraph>
                {t("fieldPage.setText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <Field.Set>
                    <Field.Legend>
                        {t("fieldPage.profile")}
                    </Field.Legend>
                    <Field.Description>
                        {t("fieldPage.profileText")}
                    </Field.Description>
                    <Field>
                        <Field.Title>
                            {t("fieldPage.fullName")}
                        </Field.Title>
                        <TextInput
                            spreadBehaviour="stretch"
                        />
                    </Field>
                </Field.Set>
            </Preview>
            <CodeBlock
                code={SET_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("fieldPage.groupTitle")}
            id="group"
        >
            <Paragraph>
                {t("fieldPage.groupText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <Field.Group
                    orientation="horizontal"
                >
                    <Field>
                        <Field.Title>
                            {t("fieldPage.firstName")}
                        </Field.Title>
                        <TextInput
                            spreadBehaviour="stretch"
                        />
                    </Field>
                    <Field>
                        <Field.Title>
                            {t("fieldPage.lastName")}
                        </Field.Title>
                        <TextInput
                            spreadBehaviour="stretch"
                        />
                    </Field>
                </Field.Group>
            </Preview>
            <CodeBlock
                code={GROUP_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("fieldPage.orientationTitle")}
            id="orientation"
        >
            <Paragraph>
                {t("fieldPage.orientationText")}
            </Paragraph>
            <Preview
                caption={t("fieldPage.orientationCaption")}
                isStacked={true}
            >
                {
                    ORIENTATIONS.map((orientation) => <Field
                        orientation={orientation}
                        key={orientation}
                    >
                        <Field.Content>
                            <Field.Title>
                                {`orientation="${orientation}"`}
                            </Field.Title>
                            <Field.Description>
                                {t(`fieldPage.${orientation}Text`)}
                            </Field.Description>
                        </Field.Content>
                        <TextInput
                            spreadBehaviour="stretch"
                        />
                    </Field>)
                }
            </Preview>
            <CodeBlock
                code={ORIENTATION_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("fieldPage.contentTitle")}
            id="content"
        >
            <Paragraph>
                {t("fieldPage.contentText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <Field
                    orientation="horizontal"
                >
                    <Field.Content>
                        <Field.Title>
                            {t("fieldPage.weeklyTitle")}
                        </Field.Title>
                        <Field.Description>
                            {t("fieldPage.weeklyText")}
                        </Field.Description>
                    </Field.Content>
                    <Switch
                        onPress={handleToggleWeekly}
                        isChecked={isWeeklyOn}
                    />
                </Field>
            </Preview>
            <CodeBlock
                code={CONTENT_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("fieldPage.errorTitle")}
            id="error"
        >
            <Paragraph>
                {t("fieldPage.errorText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <Field>
                    <Field.Title>
                        {t("fieldPage.newPassword")}
                    </Field.Title>
                    <TextInput
                        onChangeText={setPassword}
                        spreadBehaviour="stretch"
                        value={password}
                        type="danger"
                    />
                    <Field.Error
                        errors={[
                            t("fieldPage.ruleLength"),
                            t("fieldPage.ruleDigit")
                        ]}
                    />
                </Field>
                <Field>
                    <Field.Title>
                        {t("fieldPage.username")}
                    </Field.Title>
                    <TextInput
                        spreadBehaviour="stretch"
                        type="danger"
                    />
                    <Field.Error
                        errors={[
                            t("fieldPage.ruleTaken")
                        ]}
                    />
                </Field>
            </Preview>
            <CodeBlock
                code={ERROR_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("fieldPage.cardTitle")}
            id="card"
        >
            <Paragraph>
                {t("fieldPage.cardText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <Field.Set>
                    <Field.Legend>
                        {t("fieldPage.visibility")}
                    </Field.Legend>
                    {renderVisibility()}
                </Field.Set>
            </Preview>
            <CodeBlock
                code={CARD_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("fieldPage.separatorTitle")}
            id="separator"
        >
            <Paragraph>
                {t("fieldPage.separatorText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <Field.Group>
                    <Field.Set>
                        <Field.Legend>
                            {t("fieldPage.profile")}
                        </Field.Legend>
                    </Field.Set>
                    <Separator
                        title={t("fieldPage.dividerTitle")}
                    />
                    <Field.Set>
                        <Field.Legend>
                            {t("fieldPage.notifications")}
                        </Field.Legend>
                    </Field.Set>
                </Field.Group>
            </Preview>
            <CodeBlock
                code={SEPARATOR_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("fieldPage.a11yName"),
                    t("fieldPage.a11yDescription"),
                    t("fieldPage.a11yInvalid"),
                    t("fieldPage.a11yOwnTitle"),
                    t("fieldPage.a11ySet"),
                    t("fieldPage.a11yDisabled")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("fieldPage.propChildren"),
                        type: "ReactNode",
                        name: "children"
                    },
                    {
                        type: "\"vertical\" | \"horizontal\" | \"responsive\"",
                        description: t("fieldPage.propOrientation"),
                        defaultValue: "\"vertical\"",
                        name: "orientation"
                    },
                    {
                        description: t("fieldPage.propIsInvalid"),
                        defaultValue: "false",
                        name: "isInvalid",
                        type: "boolean"
                    },
                    {
                        description: t("fieldPage.propIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("fieldPage.propCustomTheme"),
                        name: "customTheme"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("fieldPage.propsNote")}
            </Paragraph>
            <Paragraph>
                {t("fieldPage.partsText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("fieldPage.propErrorErrors"),
                        type: "Array<string>",
                        name: "errors"
                    },
                    {
                        type: "\"vertical\" | \"horizontal\" | \"responsive\"",
                        description: t("fieldPage.propGroupOrientation"),
                        name: "orientation"
                    }
                ]}
            />
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
const stylesheet = StyleSheet.create({
    footer: {
        justifyContent: "flex-end",
        flexDirection: "row"
    },
    row: {
        flexDirection: "row"
    },
    choice: {
        flexBasis: 0,
        minWidth: 0,
        flexGrow: 1
    }
});
export default FieldPage;
